// src/components/planner/modals/TaskModal.tsx

import React, { useState } from 'react';
import { X, Save } from 'lucide-react';

const API_BASE_URL = '/api';

interface TaskModalProps {
    task?: any;
    subjectOptions: string[];
    taskGroups: any[];
    onClose: () => void;
    onSave: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const TaskModal: React.FC<TaskModalProps> = ({ task, subjectOptions, taskGroups, onClose, onSave, onShowToast }) => {
    const [formData, setFormData] = useState({
        title:    task?.title    || '',
        subject:  task?.subject  || '',
        group:    task?.group    || '',
        priority: task?.priority || 'media',
        dueDate:  task?.dueDate  || '',
        notes:    task?.notes    || ''
    });
    const [loading, setLoading] = useState(false);

    const getGroupColor = (name: string) =>
        taskGroups.find(g => g.name === name)?.color || '#6b7280';

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.title.trim()) {
            onShowToast('Preencha o título da tarefa', 'error');
            return;
        }
        setLoading(true);
        try {
            const token = localStorage.getItem('accessToken');
            const method = task ? 'PATCH' : 'POST';
            const url = task
                ? `${API_BASE_URL}/planner/tasks/${task._id}`
                : `${API_BASE_URL}/planner/tasks`;

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    ...formData,
                    subject: formData.subject || null,
                    group:   formData.group   || null,
                    dueDate: formData.dueDate || null
                })
            });

            if (response.ok) {
                onShowToast(task ? 'Tarefa atualizada!' : 'Tarefa criada!', 'success');
                onSave();
            } else {
                onShowToast('Erro ao salvar tarefa', 'error');
            }
        } catch {
            onShowToast('Erro ao salvar tarefa', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>{task ? 'Editar Tarefa' : 'Nova Tarefa'}</h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="plnr-modal-body">
                        <div className="plnr-form-group">
                            <label>Título *</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Ex: Revisar capítulo 5"
                                className="plnr-input"
                                autoFocus
                                required
                            />
                        </div>

                        <div className="plnr-form-row">
                            <div className="plnr-form-group">
                                <label>Prioridade</label>
                                <select
                                    value={formData.priority}
                                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                    className="plnr-input"
                                >
                                    <option value="alta">Alta</option>
                                    <option value="media">Média</option>
                                    <option value="baixa">Baixa</option>
                                </select>
                            </div>

                            <div className="plnr-form-group">
                                <label>Prazo (opcional)</label>
                                <input
                                    type="date"
                                    value={formData.dueDate}
                                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                                    className="plnr-input"
                                />
                            </div>
                        </div>

                        {/* Grupo com autocomplete + preview de cor */}
                        <div className="plnr-form-group">
                            <label>Grupo (opcional)</label>
                            <div className="plnr-task-group-input-wrapper">
                                {formData.group && (
                                    <span
                                        className="plnr-task-group-color-preview"
                                        style={{ backgroundColor: getGroupColor(formData.group) }}
                                    />
                                )}
                                <input
                                    type="text"
                                    value={formData.group}
                                    onChange={(e) => setFormData({ ...formData, group: e.target.value })}
                                    list="task-groups-datalist"
                                    className="plnr-input"
                                    style={formData.group ? { paddingLeft: 32 } : undefined}
                                />
                                <datalist id="task-groups-datalist">
                                    {taskGroups.map(g => (
                                        <option key={g._id} value={g.name} />
                                    ))}
                                </datalist>
                            </div>
                        </div>

                        <div className="plnr-form-group">
                            <label>Matéria (opcional)</label>
                            <select
                                value={formData.subject}
                                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                className="plnr-input"
                            >
                                <option value="">Sem matéria</option>
                                {subjectOptions.map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        </div>

                        <div className="plnr-form-group">
                            <label>Notas (opcional)</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Anotações sobre a tarefa..."
                                className="plnr-textarea"
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className="plnr-modal-footer">
                        <button type="button" className="plnr-btn secondary" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="plnr-btn primary" disabled={loading}>
                            <Save size={16} />
                            {loading ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TaskModal;
